<template>
  <div class="chat-widget" v-if="chatId" ref="widgetRoot">
    <button class="chat-widget__toggle" @click="handleToggle" :title="isOpen ? 'Închide chat' : 'Deschide chat'">
      <i class="mdi mdi-chat" aria-hidden="true"></i>
      <span v-if="unreadCount > 0" class="chat-widget__badge">{{ unreadCount }}</span>
    </button>

    <div v-if="isOpen" class="chat-widget__panel">
      <div class="chat-widget__header">
        <span class="chat-widget__header-title">Suport</span>
        <button class="chat-widget__header-close" @click="handleClose" title="Minimizează">×</button>
      </div>

      <div class="chat-widget__messages" ref="messagesContainer">
        <div
          v-for="msg in allMessages"
          :key="msg.id ?? ('sys-' + msg._sysId)"
          :class="[
            'chat-widget__message',
            msg.sender_type === 'system'
              ? 'chat-widget__message--system'
              : msg.sender_type === 'user'
                ? 'chat-widget__message--user'
                : 'chat-widget__message--admin'
          ]"
        >
          <span class="chat-widget__message-text">{{ msg.text }}</span>
          <span v-if="msg.sender_type !== 'system'" class="chat-widget__message-time">{{ formatTime(msg.created_at) }}</span>
        </div>

        <div v-if="messages.length === 0 && systemMessages.length === 0" class="chat-widget__empty chat-widget__empty--invite">
          Trimiteți un mesaj echipei de suport — un feedback, o idee sau o opinie. Suntem aici să ascultăm!
        </div>
        <div v-else-if="lastRealSenderType === 'user'" class="chat-widget__empty chat-widget__empty--waiting">
          <i class="mdi mdi-clock-outline" aria-hidden="true"></i> Mesajul dvs. a fost trimis. Așteptăm un răspuns din partea echipei de suport…
        </div>
      </div>

      <template v-if="chatStatus === 'open'">
        <div class="chat-widget__input-row">
          <input
            v-model="newMessage"
            class="chat-widget__input"
            placeholder="Scrieți un mesaj..."
            @keyup.enter="sendMessage"
          />
          <button class="chat-widget__send-btn" @click="sendMessage" :disabled="!newMessage.trim()">
            Trimite
          </button>
        </div>

        <div v-if="confirmClose" class="chat-widget__confirm-close">
          <span class="chat-widget__confirm-close-text">Închizi sesiunea de chat?</span>
          <button class="chat-widget__confirm-close-yes" @click="confirmCloseChat">Da, închide</button>
          <button class="chat-widget__confirm-close-no" @click="confirmClose = false">Anulează</button>
        </div>
        <button v-else class="chat-widget__close-chat" @click="messages.length > 0 ? confirmClose = true : closeChat()">
          Închide sesiunea
        </button>
      </template>
    </div>
  </div>
</template>

<script>
export default {
  name: 'FloatingChatWidget',

  data() {
    return {
      chatId: null,
      chatStatus: 'open',
      isOpen: false,
      isContactChat: false,
      hasUserSentMessage: false,
      confirmClose: false,
      messages: [],
      systemMessages: [],
      _sysCounter: 0,
      newMessage: '',
      pollingInterval: null,
      pollIntervalMs: 10000,
    };
  },

  computed: {
    unreadCount() {
      return this.messages.filter(m => m.sender_type === 'admin' && !m.read_at).length;
    },
    allMessages() {
      return [...this.messages, ...this.systemMessages].sort((a, b) => {
        const ta = a.created_at ? new Date(a.created_at).getTime() : Date.now();
        const tb = b.created_at ? new Date(b.created_at).getTime() : Date.now();
        return ta - tb;
      });
    },
    lastRealSenderType() {
      if (this.messages.length === 0) return null;
      return this.messages[this.messages.length - 1].sender_type;
    },
  },

  mounted() {
    const storedId = window.localStorage.getItem('chat_id');
    if (storedId) {
      this.chatId = parseInt(storedId, 10);
      this.isContactChat = window.localStorage.getItem('chat_is_contact') === '1';
      this.fetchMessages();
      this.startPolling();
    }
    document.addEventListener('mousedown', this.onDocumentClick);
    window.addEventListener('open-contact-chat', this.openContactChat);
  },

  beforeUnmount() {
    this.stopPolling();
    document.removeEventListener('mousedown', this.onDocumentClick);
    window.removeEventListener('open-contact-chat', this.openContactChat);
  },

  methods: {
    handleToggle() {
      if (this.chatStatus === 'closed') {
        this.clearChat();
        return;
      }
      this.isOpen = !this.isOpen;
      if (this.isOpen) {
        this.$nextTick(() => this.scrollToBottom());
      }
    },

    handleClose() {
      if (this.chatStatus === 'closed') {
        this.clearChat();
        return;
      }
      this.confirmClose = false;
      if (this.isContactChat && !this.hasUserSentMessage) {
        this.deleteEmptyContactChat();
        return;
      }
      this.isOpen = false;
    },

    onDocumentClick(event) {
      if (!this.isOpen) return;
      const root = this.$refs.widgetRoot;
      if (root && !root.contains(event.target)) {
        this.confirmClose = false;
        if (this.chatStatus === 'closed') {
          this.clearChat();
        } else if (this.isContactChat && !this.hasUserSentMessage) {
          this.deleteEmptyContactChat();
        } else {
          this.isOpen = false;
        }
      }
    },

    async fetchMessages() {
      if (!this.chatId) return;

      try {
        const response = await window.axios.get(`/chat/${this.chatId}/messages`);
        const data = response.data;
        this.messages = data.messages || [];

        if (!this.hasUserSentMessage && this.messages.some(m => m.sender_type === 'user')) {
          this.hasUserSentMessage = true;
        }

        if (data.status === 'closed' && this.chatStatus !== 'closed') {
          this.chatStatus = 'closed';
          this.addSystemMessage('Conversația a fost închisă de administrator.');
          this.stopPolling();
        } else {
          this.chatStatus = data.status || 'open';
        }

        this.$nextTick(() => this.scrollToBottom());
      } catch (err) {
        if (err.response?.status === 404) {
          this.clearChat();
        }
      }
    },

    async sendMessage() {
      const text = this.newMessage.trim();
      if (!text || !this.chatId) return;

      try {
        const response = await window.axios.post(`/chat/${this.chatId}/send`, { text });
        this.messages.push(response.data);
        this.hasUserSentMessage = true;
        this.newMessage = '';
        this.$nextTick(() => this.scrollToBottom());
      } catch (err) {
        if (err.response?.status === 422) {
          this.chatStatus = 'closed';
          this.addSystemMessage('Conversația a fost închisă.');
          this.stopPolling();
        }
      }
    },

    async closeChat() {
      if (!this.chatId) return;

      if (this.isContactChat && !this.hasUserSentMessage) {
        await this.deleteEmptyContactChat();
        return;
      }

      try {
        await window.axios.post(`/chat/${this.chatId}/close`);
      } catch {
        // ignore – proceed with local close regardless
      }

      this.chatStatus = 'closed';
      this.addSystemMessage('Ați închis sesiunea de chat.');
      this.stopPolling();
    },

    async confirmCloseChat() {
      this.confirmClose = false;
      await this.closeChat();
    },

    async openContactChat() {
      if (this.chatId) {
        this.isOpen = true;
        this.$nextTick(() => this.scrollToBottom());
        return;
      }

      try {
        const response = await window.axios.post('/chat');
        const chatId = response.data.chat_id;
        window.localStorage.setItem('chat_id', chatId);
        window.localStorage.setItem('chat_is_contact', '1');
        this.chatId = chatId;
        this.isContactChat = true;
        this.hasUserSentMessage = false;
        this.isOpen = true;
        this.fetchMessages();
        this.startPolling();
      } catch {
        // ignore
      }
    },

    async deleteEmptyContactChat() {
      const chatId = this.chatId;
      try {
        await window.axios.delete(`/chat/${chatId}`);
      } catch {
        // best-effort deletion – proceed with local cleanup regardless
      }
      this.clearChat();
    },

    addSystemMessage(text) {
      this.systemMessages.push({
        _sysId: ++this._sysCounter,
        sender_type: 'system',
        text,
        created_at: new Date().toISOString(),
      });
      this.$nextTick(() => this.scrollToBottom());
    },

    clearChat() {
      window.localStorage.removeItem('chat_id');
      window.localStorage.removeItem('chat_is_contact');
      this.chatId = null;
      this.isOpen = false;
      this.isContactChat = false;
      this.hasUserSentMessage = false;
      this.confirmClose = false;
      this.messages = [];
      this.systemMessages = [];
      this.chatStatus = 'open';
      this.stopPolling();
    },

    startPolling() {
      this.pollingInterval = setInterval(() => {
        this.fetchMessages();
      }, this.pollIntervalMs);
    },

    stopPolling() {
      if (this.pollingInterval) {
        clearInterval(this.pollingInterval);
        this.pollingInterval = null;
      }
    },

    scrollToBottom() {
      const container = this.$refs.messagesContainer;
      if (container) {
        container.scrollTop = container.scrollHeight;
      }
    },

    formatTime(dateStr) {
      if (!dateStr) return '';
      return new Date(dateStr).toLocaleTimeString('ro-RO', { hour: '2-digit', minute: '2-digit' });
    },
  },
};
</script>
